import React, { useState, useRef, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import {
  Layout as LayoutIcon,
  CurrencyDollar,
  CreditCard,
  TrendUp,
  Users,
  Gear,
  User,
  Coins,
  Sun,
  Moon,
  CaretLeft,
  CaretRight,
  Flame,
  Play,
  SignOut,
} from "@phosphor-icons/react";
import { useData } from "../context/DataContext";
import { useTheme } from "../context/ThemeContext";
import { useLanguage } from "../context/LanguageContext";
import OnboardingModal from "./OnboardingModal";
import UserProfileDrawer from "./UserProfileDrawer";
import AuthModal from "./AuthModal";
// @ts-ignore - authService is properly exported from ../lib/auth
import { authService } from "../lib/auth";
import { testPocketBaseConnection } from "../lib/pocketbase";
import { setDemoMode, isDemoMode } from "../utils/demoData";

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const { getCurrentUser, getDashboardData, dispatch } = useData();
  const { theme, toggleTheme, isCollapsed, toggleCollapsed } = useTheme();
  const { t } = useLanguage();
  const currentUser = getCurrentUser();
  const dashboardData = getDashboardData();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showProfileDrawer, setShowProfileDrawer] = useState(false);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(true); // Set to true for localStorage mode

  const mobileNavRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef<number>(0);

  const handleGetStarted = () => {
    // Just show the onboarding modal - don't create user yet
    setShowOnboarding(true);
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    // The onboarding modal will handle creating the user and transactions
  };

  const handleDemoMode = () => {
    setDemoMode(true);
    dispatch({ type: "SET_DEMO_MODE", payload: true });
  };

  const handleExitDemoMode = () => {
    // Clear demo state from localStorage
    try {
      localStorage.removeItem("runway-demo-state");
    } catch (error) {
      console.error("Error clearing demo state:", error);
    }

    setDemoMode(false);
    dispatch({ type: "SET_DEMO_MODE", payload: false });
  };

  // Check authentication status on mount and when auth changes
  useEffect(() => {
    const checkAuth = () => {
      setIsAuthenticated(authService.isAuthenticated());
    };

    checkAuth();

    // Test PocketBase connection
    testPocketBaseConnection();

    // Listen for auth changes
    const unsubscribe = authService.getCurrentUser()
      ? () => {} // If we had a real auth listener, we'd use it here
      : () => {};

    return unsubscribe;
  }, []);

  const handleAuthSuccess = () => {
    setIsAuthenticated(true);
    setShowAuthModal(false);

    // Clear localStorage when user successfully authenticates
    // This ensures new users start with a clean slate
    try {
      localStorage.removeItem("runway-app-state");
    } catch (error) {
      console.error("Error clearing localStorage:", error);
    }
  };

  const handleSignOut = () => {
    setIsAuthenticated(false);
  };

  // Mobile navigation drag handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;

    const currentY = e.touches[0].clientY;
    const deltaY = currentY - touchStartY.current;
    const newOffset = Math.max(0, Math.min(100, deltaY));
    setDragOffset(newOffset);

    if (newOffset > 50) {
      setShowMobileNav(false);
    }
  };

  const handleTouchEnd = () => {
    if (!showMobileNav) {
      setDragOffset(0);
      setIsDragging(false);
    }
  };

  const navigation = [
    { name: t("nav.dashboard"), href: "/", icon: LayoutIcon },
    { name: t("nav.income"), href: "/income", icon: CurrencyDollar },
    { name: t("nav.expenses"), href: "/expenses", icon: CreditCard },
    { name: t("nav.assets"), href: "/assets", icon: Coins },
    { name: t("nav.projections"), href: "/projections", icon: TrendUp },
    { name: t("nav.fire"), href: "/fire", icon: Flame },
    { name: t("nav.partner"), href: "/collaboration", icon: Users },
    { name: t("nav.settings"), href: "/settings", icon: Gear },
  ];

  // Special case for PocketBase test page - show it without authentication
  if (location.pathname === "/pocketbase-test") {
    return <>{children}</>;
  }

  // Disable authentication for localStorage-only mode
  const isLocalStorageMode = true;

  if (!isAuthenticated && !isLocalStorageMode) {
    return (
      <>
        <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-600 rounded-lg flex items-center justify-center mx-auto mb-6">
                <span className="text-white font-mono font-bold text-4xl leading-none">
                  R
                </span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {t("dashboard.welcome")}
              </h1>
              <p className="text-gray-700 dark:text-gray-300 mb-8 max-w-md">
                {t("dashboard.description")}
              </p>
              <button
                className="btn-primary"
                onClick={() => setShowAuthModal(true)}>
                Sign In
              </button>
            </div>
          </div>
        </div>
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onSuccess={handleAuthSuccess}
        />
      </>
    );
  }

  // If authenticated but no current user, show onboarding
  if (!currentUser) {
    return (
      <>
        <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-600 rounded-lg flex items-center justify-center mx-auto mb-6">
                <span className="text-white font-mono font-bold text-4xl leading-none">
                  R
                </span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {t("dashboard.welcome")}
              </h1>
              <p className="text-gray-700 dark:text-gray-300 mb-8 max-w-md">
                {t("dashboard.description")}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  className="btn-primary flex items-center justify-center space-x-2"
                  onClick={handleGetStarted}>
                  <span>Get Started</span>
                </button>
                <button
                  className="btn-secondary flex items-center justify-center space-x-2"
                  onClick={handleDemoMode}>
                  <Play className="w-4 h-4" weight="duotone" />
                  <span>Try Demo</span>
                </button>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-4 max-w-sm">
                Demo mode lets you explore the app with sample data. Your
                changes won't be saved and will reset when you close the
                browser.
              </p>
            </div>
          </div>
        </div>
        <OnboardingModal
          isOpen={showOnboarding}
          onClose={() => setShowOnboarding(false)}
          onComplete={handleOnboardingComplete}
        />
        <UserProfileDrawer
          isOpen={showProfileDrawer}
          onClose={() => setShowProfileDrawer(false)}
          onSignOut={handleSignOut}
        />
      </>
    );
  }

  // Get page-specific description
  const getPageDescription = () => {
    const currentPath = location.pathname;
    switch (currentPath) {
      case "/":
      case "/dashboard":
        return t("dashboard.description");
      case "/income":
        return t("income.description");
      case "/expenses":
        return t("expenses.description");
      case "/assets":
        return t("assets.description");
      case "/projections":
        return t("projections.description");
      case "/fire":
        return t("fire.description");
      case "/collaboration":
        return t("partner.description");
      case "/settings":
        return t("settings.description");
      default:
        return t("dashboard.description");
    }
  };

  return (
    <>
      <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
        {/* Mobile Floating Action Button */}
        <div className="lg:hidden fixed bottom-4 right-4 z-40">
          <button
            onClick={() => setShowMobileNav(true)}
            className="w-14 h-14 bg-primary-600 rounded-full shadow-lg flex items-center justify-center hover:bg-primary-700 transition-colors">
            <div className="flex flex-col space-y-1">
              <div className="w-6 h-0.5 bg-white rounded-full"></div>
              <div className="w-6 h-0.5 bg-white rounded-full"></div>
              <div className="w-6 h-0.5 bg-white rounded-full"></div>
            </div>
          </button>
        </div>

        {/* Sidebar */}
        <div
          className={`sidebar sticky top-0 h-screen hidden lg:block ${
            isCollapsed ? "sidebar-collapsed" : "sidebar-expanded"
          }`}>
          <div className="flex flex-col h-full">
            {/* Logo */}
            <div className="flex items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-mono font-bold text-lg leading-none">
                    R
                  </span>
                </div>
                {!isCollapsed && (
                  <span className="text-xl font-bold text-gray-900 dark:text-white">
                    Runway
                  </span>
                )}
              </div>
            </div>

            {/* Navigation */}
            <nav
              className={`flex-1 py-6 space-y-2 ${
                isCollapsed ? "px-1" : "px-2"
              }`}
              role="menubar"
              aria-label="Main navigation">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                const isComingSoon = false; // No items are coming soon currently
                const isBeta =
                  item.href === "/fire" ||
                  item.href === "/projections" ||
                  item.href === "/collaboration";
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`nav-item ${
                      isActive ? "nav-item-active" : "nav-item-inactive"
                    } ${isCollapsed ? "justify-center" : ""}`}
                    title={isCollapsed ? item.name : undefined}
                    data-tooltip={isCollapsed ? item.name : undefined}
                    aria-current={isActive ? "page" : undefined}
                    role="menuitem">
                    <div className="flex-shrink-0">
                      <item.icon className="w-6 h-6" weight="duotone" />
                    </div>
                    {!isCollapsed && (
                      <div className="flex items-center space-x-2">
                        <span className="ml-3">{item.name}</span>
                        {isComingSoon && (
                          <span className="text-xs bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-200 px-2 py-1 rounded-md whitespace-nowrap font-medium">
                            Soon
                          </span>
                        )}
                        {isBeta && (
                          <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200 px-2 py-1 rounded-md whitespace-nowrap font-medium">
                            Beta
                          </span>
                        )}
                      </div>
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* User Profile */}
            <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-700">
              {!isCollapsed ? (
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setShowProfileDrawer(true)}
                    className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center hover:bg-primary-700 transition-colors cursor-pointer">
                    <span className="text-sm font-medium text-white">
                      {currentUser?.avatar || "U"}
                    </span>
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {currentUser?.name || "User"}
                    </p>
                    <p className="text-xs text-gray-700 dark:text-gray-400 truncate">
                      {currentUser?.email || "user@example.com"}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex justify-center">
                  <button
                    onClick={() => setShowProfileDrawer(true)}
                    className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center hover:bg-primary-700 transition-colors cursor-pointer">
                    <span className="text-sm font-medium text-white">
                      {currentUser?.avatar || "U"}
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden lg:ml-0">
          {/* Header */}
          <header className="header px-3 sm:px-4 lg:px-6 py-3 sm:py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 lg:space-x-4">
                <button
                  onClick={toggleCollapsed}
                  className="btn-icon lg:block hidden"
                  aria-label={
                    isCollapsed ? "Expand sidebar" : "Collapse sidebar"
                  }>
                  {isCollapsed ? (
                    <CaretRight
                      className="w-5 h-5 text-gray-600 dark:text-gray-400"
                      weight="duotone"
                    />
                  ) : (
                    <CaretLeft
                      className="w-5 h-5 text-gray-600 dark:text-gray-400"
                      weight="duotone"
                    />
                  )}
                </button>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center space-x-3">
                    <h1 className="text-base sm:text-lg lg:text-2xl font-bold text-gray-900 dark:text-white truncate">
                      {navigation.find(
                        (item) => item.href === location.pathname
                      )?.name || "Dashboard"}
                    </h1>
                    {isDemoMode() && (
                      <span className="text-xs bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-200 px-2 py-1 rounded-full font-medium">
                        {t("settings.demoMode")}
                      </span>
                    )}
                  </div>
                  <p className="text-xs sm:text-sm lg:text-base text-gray-700 dark:text-gray-400 hidden sm:block">
                    {getPageDescription()}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-1 sm:space-x-2 lg:space-x-3">
                {dashboardData && (
                  <div
                    className={`hidden sm:flex items-center space-x-2 rounded-lg px-2 sm:px-3 py-1 sm:py-2 ${
                      dashboardData.currentBalance >= 0
                        ? "bg-green-100 dark:bg-green-900/30"
                        : "bg-red-100 dark:bg-red-900/30"
                    }`}>
                    <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-400">
                      Balance:
                    </span>
                    <span
                      className={`text-xs sm:text-sm font-semibold ${
                        dashboardData.currentBalance >= 0
                          ? "text-green-700 dark:text-green-300"
                          : "text-red-700 dark:text-red-300"
                      }`}>
                      {dashboardData.currentBalance >= 0
                        ? `$${dashboardData.currentBalance.toLocaleString()}`
                        : `-$${Math.abs(
                            dashboardData.currentBalance
                          ).toLocaleString()}`}
                    </span>
                  </div>
                )}
                {/* Exit Demo Mode Button */}
                {isDemoMode() && (
                  <button
                    onClick={handleExitDemoMode}
                    className="btn-icon p-1.5 sm:p-2 text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300"
                    aria-label="Exit demo mode">
                    <SignOut
                      className="w-4 h-4 sm:w-5 sm:h-5"
                      weight="duotone"
                    />
                  </button>
                )}

                {/* Theme Toggle */}
                <button
                  onClick={toggleTheme}
                  className="btn-icon p-1.5 sm:p-2"
                  aria-label={`Switch to ${
                    theme === "light" ? "dark" : "light"
                  } mode`}>
                  {theme === "light" ? (
                    <Moon
                      className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-400"
                      weight="duotone"
                    />
                  ) : (
                    <Sun
                      className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-400"
                      weight="duotone"
                    />
                  )}
                </button>

                <button
                  onClick={() => setShowProfileDrawer(true)}
                  className="btn-icon p-1.5 sm:p-2"
                  aria-label="Open profile settings">
                  <User
                    className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-400"
                    weight="duotone"
                  />
                </button>
                <Link
                  to="/settings"
                  className="btn-icon p-1.5 sm:p-2"
                  aria-label="Settings">
                  <Gear
                    className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-400"
                    weight="duotone"
                  />
                </Link>
              </div>
            </div>
          </header>

          {/* Page Content */}
          <main className="flex-1 overflow-auto p-3 sm:p-4 lg:p-6 main-content pb-20 lg:pb-6">
            {children}
          </main>
        </div>

        {/* User Profile Drawer */}
        <UserProfileDrawer
          isOpen={showProfileDrawer}
          onClose={() => setShowProfileDrawer(false)}
          onSignOut={handleSignOut}
        />

        {/* Mobile Navigation Drawer */}
        <div
          className={`lg:hidden fixed inset-0 z-50 transition-opacity duration-300 ${
            showMobileNav
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none"
          }`}>
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-300"
            onClick={() => setShowMobileNav(false)}
          />

          {/* Bottom Sheet */}
          <div
            ref={mobileNavRef}
            className="absolute bottom-0 left-0 right-0 bg-stone-50 dark:bg-gray-800 rounded-t-2xl shadow-2xl transform transition-all duration-300 ease-out"
            style={{
              transform: `translateY(${showMobileNav ? dragOffset : 100}px)`,
              transition: isDragging
                ? "none"
                : "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}>
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
            </div>

            {/* Navigation Items */}
            <div className="px-4 pb-6">
              <div className="grid grid-cols-2 gap-3 mb-6">
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  const isComingSoon = false; // No items are coming soon currently
                  const isBeta =
                    item.href === "/fire" ||
                    item.href === "/projections" ||
                    item.href === "/collaboration";

                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setShowMobileNav(false)}
                      className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all duration-200 ${
                        isActive
                          ? "border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300"
                          : "border-gray-200 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-600"
                      }`}>
                      <item.icon className="w-5 h-5 mb-1" weight="duotone" />
                      <span className="text-xs font-medium text-center leading-tight">
                        {item.name}
                      </span>
                      {isComingSoon && (
                        <span className="text-xs bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-200 px-1 py-0.5 rounded text-xs mt-1 font-medium">
                          Soon
                        </span>
                      )}
                      {isBeta && (
                        <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200 px-1 py-0.5 rounded text-xs mt-1 font-medium">
                          Beta
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>

              {/* User Profile Section */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => {
                      setShowMobileNav(false);
                      setShowProfileDrawer(true);
                    }}
                    className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-white">
                      {currentUser?.avatar || "U"}
                    </span>
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {currentUser?.name || "User"}
                    </p>
                    <p className="text-xs text-gray-700 dark:text-gray-400 truncate">
                      {currentUser?.email || "user@example.com"}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowMobileNav(false);
                      setShowProfileDrawer(true);
                    }}
                    className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                    <User className="w-5 h-5" weight="duotone" />
                  </button>
                </div>
              </div>

              {/* Exit Demo Mode Button (Mobile) */}
              {isDemoMode() && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                  <button
                    onClick={() => {
                      setShowMobileNav(false);
                      handleExitDemoMode();
                    }}
                    className="w-full flex items-center justify-center space-x-2 p-3 text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 bg-orange-50 hover:bg-orange-100 dark:bg-orange-900/20 dark:hover:bg-orange-900/30 rounded-lg transition-colors">
                    <SignOut className="w-4 h-4" weight="duotone" />
                    <span className="text-sm font-medium">Exit Demo Mode</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Layout;
